import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { loginSchema } from '@en3/shared';
import { toDisplayMessage } from '@/api/errors';
import { useLogin } from '@/api/hooks/useAuth';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { serverFieldErrors, zodFieldErrors } from '@/lib/form';

interface LocationState {
  from?: string;
}

/**
 * 登录页。
 *
 * 校验规则直接复用 `@en3/shared` 的 `loginSchema` —— 前端提示与后端报错用的是同一份 zod，
 * 不会出现"前端说能过、后端说不行"。服务端返回的字段级错误也会落回同一个展示通道。
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    const parsed = loginSchema.safeParse({ email, password });
    if (!parsed.success) {
      setErrors(zodFieldErrors(parsed.error));
      return;
    }
    setErrors({});

    try {
      await login.mutateAsync(parsed.data);
      const from = (location.state as LocationState | null)?.from;
      navigate(from ?? ROUTES.home, { replace: true });
    } catch (error) {
      setErrors(serverFieldErrors(error));
      setFormError(toDisplayMessage(error));
    }
  }

  return (
    <section className="mx-auto max-w-md px-5 py-10">
      <h1 className="text-2xl font-bold">登录</h1>
      <p className="mt-1 text-sm text-muted-foreground">登录后生词本、复习记录与水平报告会绑定到你的账号。</p>

      <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-4">
        <Field id="email" label="邮箱" error={errors.email}>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            placeholder="you@example.com"
            value={email}
            aria-invalid={errors.email ? true : undefined}
            aria-describedby={errors.email ? 'email-error' : undefined}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>

        <Field id="password" label="密码" error={errors.password}>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            placeholder="请输入密码"
            value={password}
            aria-invalid={errors.password ? true : undefined}
            aria-describedby={errors.password ? 'password-error' : undefined}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>

        {formError && (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {formError}
          </p>
        )}

        <Button type="submit" className="w-full" loading={login.isPending}>
          登录
        </Button>
      </form>

      <p className="mt-6 text-sm text-muted-foreground">
        还没有账号？
        <Link className="ml-1 text-primary underline underline-offset-4" to={ROUTES.register}>
          立即注册
        </Link>
      </p>
      <p className="mt-2 text-xs text-muted-foreground">
        提示：浏览教材与查词无需登录；生词本、复习与水平报告需要登录后才会保存。
      </p>
    </section>
  );
}
